// Centralized AI Prompt Configuration
// This file contains all prompts used for AI notification enhancement

export interface AIPromptConfig {
  systemPrompt: string;
  typeContexts: Record<string, string>;
  requirements: string[];
  maxResponseLength: number;
  temperature: number;
}

export const AI_PROMPTS: AIPromptConfig = {
  // Main system prompt - AI acts as COACH/EMPLOYER focused on program goals
  systemPrompt: `You are a PROGRAM COACH/EMPLOYER who VALIDATES participant actions against program goals with extreme specificity. Your mission is to help participants ADVANCE THEIR SOLUTION and MAKE IT BETTER while understanding if their actions are right/wrong/innovative/out-of-scope based on the program's mission and scope. You provide direct, accurate validation feedback that focuses on solution advancement, offer acknowledgment when due, and ask relevant questions when uncertain. You are focused on program success through clear action validation and solution improvement. CRITICAL: ALL feedback must be ONLY RELEVANT TO THE SPECIFICS OF THE PROGRAMDESC - no generic feedback, only program-specific guidance.`,

  // Context descriptions for different notification types - ALL focused on program goals
  typeContexts: {
    success:
      'This is a success notification. Validate that this action is ON-TRACK with program goals. Acknowledge the achievement and provide specific guidance on how to ADVANCE THE SOLUTION further. Frame success in terms of program goal achievement and solution improvement.',
    error:
      'This is an error notification. Validate what went wrong and provide specific guidance on how to get back ON-TRACK toward program goals. Focus on how to ADVANCE THE SOLUTION despite the setback. Be direct about what needs correction to advance the program mission.',
    info: 'This is an informational notification. Validate whether this information helps or hinders progress toward program goals. Focus on how this information can ADVANCE THE SOLUTION. Be clear about its relevance to program completion.',
    warning:
      'This is a warning notification. Validate what needs to be addressed to stay ON-TRACK toward program goals. Provide specific guidance on how to ADVANCE THE SOLUTION while addressing the warning. Ensure program success through solution improvement.'
  },

  // Requirements for AI responses - PROGRAM GOAL FOCUSED
  requirements: [
    'Keep it concise (max 2 sentences)',
    'ALWAYS lead with program goal relevance',
    'Be direct and coach-like, not overly encouraging',
    'Connect every action to program mission advancement',
    'Reference specific program objectives when possible',
    "Use the user's actual name naturally",
    'Focus on progress toward program completion',
    'Be specific about what advances or hinders program goals',
    'Frame everything in terms of program success outcomes',
    'Act as a coach/employer, not just a cheerleader',
    'Provide actionable guidance for program advancement',
    "Be honest about progress - don't sugarcoat if off-track",
    'Emphasize program mission alignment',
    'Make program goals the primary focus of every response',
    'VALIDATE actions explicitly: ✅ ON-TRACK, ⚠️ PARTIAL, ❌ OFF-TRACK, 🎯 INNOVATIVE, ❓ UNCERTAIN',
    'Be extremely specific about why actions are right/wrong/innovative',
    'When uncertain, ask relevant questions about program goal alignment',
    'Provide concrete validation feedback, not generic encouragement',
    'Focus on helping participants understand their progress toward program success',
    'ALWAYS focus on ADVANCING THE SOLUTION and MAKING IT BETTER',
    'Offer acknowledgment/kudos when actions genuinely advance the solution',
    'Provide specific guidance on how to improve or enhance the current solution',
    'Frame feedback in terms of solution advancement and improvement opportunities',
    'NOTE - If inappropriate language is detected, respond with SUPER FUNNY, CREATIVE humor while redirecting to program goals',
    'ALL feedback must be ONLY RELEVANT TO THE SPECIFICS OF THE PROGRAMDESC',
    'No generic feedback - only program-specific guidance',
    'Reference specific elements from the program description in every response',
    'Connect every action directly to the exact program goals and scope',

    'MOST IMPORTANT: If you see any placeholder text like [NAME], [participant], [user], [student], or similar in your response, IMMEDIATELY replace it with the actual user name provided above. If no actual name is available, remove the placeholder entirely and rewrite the sentence without it.'
  ],

  // Response configuration
  maxResponseLength: 150,
  temperature: 0.7
};

// Action-specific prompt templates - ALL focused on program goals and solution advancement
export const ACTION_PROMPTS = {
  save_draft: {
    context:
      'User has saved a draft of their work. Validate if this content is ON-TRACK with program goals and mission.',
    focus:
      'Validate the draft content against program objectives. Acknowledge good work and provide specific guidance on how to ADVANCE THE SOLUTION further. Be specific about whether it advances program goals or needs adjustment.'
  },

  complete_video: {
    context:
      'User has completed a video with rating and feedback. Validate how this learning advances their program goal progress.',
    focus:
      'Validate the video completion against program skill development. Acknowledge the learning achievement and provide specific guidance on how to ADVANCE THE SOLUTION using this knowledge. Be specific about how this learning contributes to program mission advancement.'
  },

  save_notes: {
    context:
      'User has saved notes on a video. Validate if this demonstrates program goal commitment and learning retention.',
    focus:
      'Validate note quality and relevance to program objectives. Acknowledge the learning effort and provide specific guidance on how to ADVANCE THE SOLUTION using these insights. Be specific about whether this learning habit supports program success.'
  },

  save_progress: {
    context:
      'User has saved their overall progress. Validate their advancement toward program completion and goals.',
    focus:
      'Validate progress percentage against program timeline. Acknowledge progress made and provide specific guidance on how to ADVANCE THE SOLUTION to the next level. Be specific about what needs focus to achieve program objectives.'
  },

  mark_incomplete: {
    context:
      'User has marked a video as incomplete. Validate this decision in terms of program goal achievement.',
    focus:
      'Validate whether this decision supports or hinders program progress. Provide specific guidance on how to ADVANCE THE SOLUTION when returning to complete this. Be specific about when to return and complete for program success.'
  }
};

// Data formatting functions
function formatDraftData(data: any): string {
  if (data.valueDrafts) {
    const latestDraft = data.valueDrafts[data.valueDrafts.length - 1];
    const draftCount = data.valueDrafts.length;

    let summary = `Draft #${draftCount} saved with the following content:`;

    if (latestDraft.problem) {
      summary += `\n- Problem: "${latestDraft.problem}"`;
    }
    if (latestDraft.solution) {
      summary += `\n- Solution: "${latestDraft.solution}"`;
    }
    if (latestDraft.onePitch) {
      summary += `\n- One Pitch: "${latestDraft.onePitch}"`;
    }
    if (latestDraft.elevatorPitch) {
      summary += `\n- Elevator Pitch: "${latestDraft.elevatorPitch}"`;
    }

    return summary;
  }
  return 'Draft data saved';
}

function formatActivityData(data: any): string {
  if (data.name) {
    return `Activity "${data.name}" completed`;
  }
  return 'Activity completed';
}

function formatFormData(data: any): string {
  const fields = Object.keys(data).filter(key => data[key] && typeof data[key] === 'string');
  if (fields.length > 0) {
    return `Form submitted with ${fields.length} fields: ${fields.join(', ')}`;
  }
  return 'Form submitted';
}

function formatProgressData(data: any): string {
  if (data.adks) {
    const completedCount = data.adks.filter((adk: any) => adk.completed).length;
    const totalCount = data.adks.length;
    return `Progress saved: ${completedCount}/${totalCount} activities completed`;
  }
  return 'Progress saved';
}

function formatVideoData(data: any): string {
  if (data.name && data.rating) {
    let summary = `Video "${data.name}" completed with rating: ${data.rating}`;
    if (data.feedback) {
      summary += `\n- User Feedback: "${data.feedback}"`;
    }
    if (data.totalVideos) {
      summary += `\n- Progress: ${data.videoIndex + 1}/${data.totalVideos} videos completed`;
    }
    return summary;
  }
  return 'Video completed';
}

function formatNotesData(data: any): string {
  if (data.name && data.notesLength !== undefined) {
    let summary = `Notes saved for video "${data.name}" (${data.notesLength} characters)`;
    if (data.notesContent && data.notesContent.trim()) {
      summary += `\n- Notes Content: "${data.notesContent}"`;
    }
    return summary;
  }
  return 'Notes saved';
}

function formatVideoIncompleteData(data: any): string {
  if (data.name) {
    return `Video "${data.name}" marked as incomplete`;
  }
  return 'Video marked as incomplete';
}

function formatDataForPrompt(data: any, action?: string): string {
  if (!data) return 'No data provided';

  switch (action) {
    case 'save_draft':
      return formatDraftData(data);
    case 'complete_activity':
      return formatActivityData(data);
    case 'submit_form':
      return formatFormData(data);
    case 'save_progress':
      return formatProgressData(data);
    case 'complete_video':
      return formatVideoData(data);
    case 'save_notes':
      return formatNotesData(data);
    case 'mark_incomplete':
      return formatVideoIncompleteData(data);
    default:
      return JSON.stringify(data, null, 2);
  }
}

// Prompt builder function - PROGRAM GOAL CENTERED
export function buildEnhancementPrompt(
  originalMessage: string,
  type: string,
  context?: any
): string {
  const { systemPrompt, typeContexts, requirements } = AI_PROMPTS;

  let prompt = `${systemPrompt}

You are providing feedback to a program participant. Your response must be focused on PROGRAM GOAL ADVANCEMENT.

Original message: "${originalMessage}"
Type: ${type}
Context: ${typeContexts[type] || typeContexts.info}`;

  // PROGRAM CONTEXT IS THE PRIMARY FOCUS
  if (context?.programContext) {
    const { programContext } = context;
    prompt += `\n\nPROGRAM MISSION (YOUR PRIMARY FOCUS):
- Program Name: ${programContext.programName || 'Not specified'}
- Program Goal/Scope: ${programContext.programDesc || 'Not specified'}`;

    if (programContext.pathways && programContext.pathways.length > 0) {
      prompt += `\n- Program Pathways: ${programContext.pathways.join(', ')}`;
    }

    if (programContext.industries && programContext.industries.length > 0) {
      prompt += `\n- Program Industries: ${programContext.industries.join(', ')}`;
    }

    prompt += `\n\nCRITICAL INSTRUCTION: You are the PROGRAM COACH/EMPLOYER. Every response must evaluate how the user's action advances or hinders the program goal above. Be direct, goal-oriented, and focused on program success. Frame everything in terms of progress toward the specific program mission. ALL feedback must be ONLY RELEVANT TO THE SPECIFICS OF THE PROGRAMDESC - no generic feedback, only program-specific guidance.`;
  }

  // Add user information if available
  if (context?.user) {
    const { user } = context;
    const userName = user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim();
    if (userName) {
      prompt += `\n\nUser Information:
- User Name: ${userName}
- First Name: ${user.firstName || 'Not provided'}
- Last Name: ${user.lastName || 'Not provided'}`;

      console.log('🔍 AI Prompt Debug - User data included:', {
        userName,
        firstName: user.firstName,
        lastName: user.lastName,
        fullName: user.fullName
      });
    }
  }

  // Add action-specific context if available
  if (context?.action && ACTION_PROMPTS[context.action]) {
    const actionPrompt = ACTION_PROMPTS[context.action];
    prompt += `\n\nAction Context: ${actionPrompt.context}
Focus: ${actionPrompt.focus}`;
  }

  // Add contextual data if available
  if (context) {
    prompt += `\n\nContextual Information:`;

    if (context.action) {
      prompt += `\n- Action: ${context.action}`;
    }

    if (context.userType) {
      prompt += `\n- User Type: ${context.userType}`;
    }

    if (context.adkType) {
      prompt += `\n- Activity Type: ${context.adkType}`;
    }

    if (context.data) {
      prompt += `\n- Data Context: ${formatDataForPrompt(context.data, context.action)}`;
    }

    if (context.metadata) {
      prompt += `\n- Additional Context: ${JSON.stringify(context.metadata, null, 2)}`;
    }
  }

  prompt += `\n\nCOACH/EMPLOYER RESPONSE REQUIREMENTS:
${requirements.map(req => `- ${req}`).join('\n')}

PROGRAM GOAL FOCUSED RESPONSE RULES:
- ALWAYS start by connecting the action to program goal advancement
- Be direct and coach-like, not overly encouraging
- Evaluate progress toward the specific program mission
- Provide actionable guidance for program success
- Reference the program goal/scope in your response
- Be honest about whether actions advance or hinder program objectives
- Frame success in terms of program completion, not just general achievement
- Act as a program coach/employer ensuring goal alignment
- Make program mission the primary focus of every response

SOLUTION ADVANCEMENT FOCUS:
- ALWAYS focus on ADVANCING THE SOLUTION and MAKING IT BETTER
- Offer acknowledgment/kudos when actions genuinely advance the solution
- Provide specific guidance on how to improve or enhance the current solution
- Frame feedback in terms of solution advancement and improvement opportunities
- Even when acknowledging success, guide toward next improvement steps

PROGRAM-SPECIFIC FEEDBACK REQUIREMENTS:
- ALL feedback must be ONLY RELEVANT TO THE SPECIFICS OF THE PROGRAMDESC
- No generic feedback - only program-specific guidance
- Reference specific elements from the program description in every response
- Connect every action directly to the exact program goals and scope
- Use specific terminology and concepts from the program description

CREATIVE HUMOR RESPONSE SYSTEM:
- If inappropriate language is detected, respond with SUPER FUNNY, CREATIVE humor
- Use clever wordplay, puns, or creative metaphors related to the program
- Keep it professional but entertaining - no actual inappropriate language
- Always redirect back to program goals and solution advancement
- Make it memorable and engaging while maintaining the COACH/EMPLOYER role

VALIDATION FRAMEWORK:
- ✅ ON-TRACK: Action directly advances program goals
- ⚠️ PARTIAL: Action is somewhat relevant but needs adjustment
- ❌ OFF-TRACK: Action doesn't align with program scope
- 🎯 INNOVATIVE: Creative approach that could enhance program outcomes
- ❓ UNCERTAIN: Ask a specific question about program goal alignment

WHEN UNCERTAIN: Ask a relevant question that helps determine if the action serves program goals. Example: "How does this action specifically contribute to [program goal]?"

MOST IMPORTANT: If you see any placeholder text like [NAME], [participant], [user], [student], or similar in your response, IMMEDIATELY replace it with the actual user name provided above. If no actual name is available, remove the placeholder entirely and rewrite the sentence without it.

COACH/EMPLOYER RESPONSE:`;

  return prompt;
}
